import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Loader2, Trophy, Sparkles, Droplets, Wind, Car as CarIcon, Key, ExternalLink, PoundSterling,
} from "lucide-react";

const STEPS = [
  { id: "washed", label: "Vehicle washed", icon: Droplets },
  { id: "vacuumed", label: "Vacuumed", icon: Wind },
  { id: "interior_clean", label: "Interior wiped down", icon: CarIcon },
  { id: "keys_returned", label: "Keys ready for handover", icon: Key },
];

export default function HandoverModal({ open, onClose, jobId, onCompleted }) {
  const [busy, setBusy] = useState(false);
  const [defaultSurvey, setDefaultSurvey] = useState("");
  const [job, setJob] = useState(null);
  const [state, setState] = useState({
    washed: false, vacuumed: false, interior_clean: false, keys_returned: false,
    survey_url: "", notes: "",
  });

  useEffect(() => {
    if (!open) return;
    setState({
      washed: false, vacuumed: false, interior_clean: false, keys_returned: false,
      survey_url: "", notes: "",
    });
    api.get("/settings").then((r) => setDefaultSurvey(r.data.survey_url || "")).catch(() => {});
    if (jobId) {
      api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => {});
    }
  }, [open, jobId]);

  const tickedCount = STEPS.filter(s => state[s.id]).length;
  const surveyToUse = (state.survey_url || "").trim() || defaultSurvey;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/complete`, state);
      // Auto-fire the thank-you on every configured channel — email is always
      // live in production; SMS only fires if Twilio is configured.
      let thankRes = null;
      try {
        thankRes = await api.post(`/jobs/${jobId}/send-thank-you`, { channel: "email" });
      } catch { /* swallow — thank-you is best-effort */ }
      // Best-effort SMS too (will return ok:false if Twilio dormant — fine)
      try { await api.post(`/jobs/${jobId}/send-thank-you`, { channel: "sms" }); } catch {}

      if (thankRes?.data?.ok) {
        toast.success("Handover saved · Thank-you email sent to customer");
      } else {
        toast.success("Handover saved — customer can collect");
      }
      onCompleted?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't complete job");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-up" data-testid="handover-modal">
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="label-overline">Handover</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" /> Ready for the customer
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Quick check before keys go back. Tick what's done — survey link will go out automatically.
            </p>
          </div>
          <button onClick={onClose} className="btn-outline" data-testid="handover-close-btn"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Cost summary — the customer's bill */}
          {job && (() => {
            const booked = Number(job.booked_service_amount || 0);
            const bookedName = job.booked_service_name || "Booked service";
            const extras = Number(job.approved_total || 0);
            const grand = booked + extras;
            if (grand <= 0) return null;
            return (
              <div className="rounded-lg border border-primary/40 bg-primary/[0.06] p-4" data-testid="handover-cost-summary">
                <div className="label-overline text-primary flex items-center gap-1.5">
                  <PoundSterling className="w-3.5 h-3.5" /> Customer's bill
                </div>
                <div className="mt-2 space-y-1.5 text-sm">
                  {booked > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{bookedName}</span>
                      <span className="font-mono-plex" data-testid="handover-booked-amount">£{booked.toFixed(2)}</span>
                    </div>
                  )}
                  {extras > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Additional approved work</span>
                      <span className="font-mono-plex" data-testid="handover-extras-amount">£{extras.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-border/60 pt-2 mt-1 flex items-baseline justify-between">
                    <span className="label-overline">Total to take</span>
                    <span className="font-display font-bold text-2xl tracking-tight" data-testid="handover-grand-total">
                      £{grand.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Checklist */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="label-overline">Final check</div>
              <span className="text-xs font-mono-plex text-muted-foreground" data-testid="handover-tick-count">
                {tickedCount} / {STEPS.length}
              </span>
            </div>
            <div className="space-y-2">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const checked = state[s.id];
                return (
                  <label
                    key={s.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "border-success bg-success/5" : "border-border hover:bg-muted/30"}`}
                    data-testid={`handover-tick-${s.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setState((p) => ({ ...p, [s.id]: e.target.checked }))}
                      className="w-4 h-4 rounded border-border accent-success"
                    />
                    <Icon className={`w-4 h-4 ${checked ? "text-success" : "text-muted-foreground"}`} />
                    <span className={`text-sm font-medium ${checked ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Survey URL */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-overline">Customer satisfaction survey</label>
              {defaultSurvey && (
                <span className="text-[10px] font-mono-plex text-muted-foreground">workshop default applied</span>
              )}
            </div>
            <input
              type="url"
              value={state.survey_url}
              onChange={(e) => setState((p) => ({ ...p, survey_url: e.target.value }))}
              placeholder={defaultSurvey || "https://surveys.toyota.co.uk/csi/..."}
              className="input-default font-mono-plex text-sm"
              data-testid="handover-survey-url"
            />
            {surveyToUse && (
              <a
                href={surveyToUse}
                target="_blank" rel="noreferrer"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-2"
                data-testid="handover-survey-preview"
              >
                <ExternalLink className="w-3 h-3" /> Preview link
              </a>
            )}
            <p className="text-[11px] text-muted-foreground mt-2">Set a workshop-wide default in Settings — leave blank here to use it.</p>
          </div>

          {/* Notes */}
          <div>
            <label className="label-overline">Handover notes (optional)</label>
            <textarea
              value={state.notes}
              onChange={(e) => setState((p) => ({ ...p, notes: e.target.value }))}
              rows={3}
              placeholder="e.g. Mats still drying, customer collecting after 5pm…"
              className="input-default text-sm mt-1"
              data-testid="handover-notes"
            />
          </div>

          <div className="rounded-lg bg-primary/10 border border-primary/30 p-3 flex items-start gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>The customer will see a thank-you video, the survey link and a clear "All done" page on their original quote URL.</span>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-border">
          <button onClick={onClose} className="btn-outline" data-testid="handover-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="handover-complete-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
            Complete &amp; send handover
          </button>
        </div>
      </div>
    </div>
  );
}
